import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRightLeft, Bell, CalendarClock, CheckCheck, Globe, Server, Settings, WifiOff, XCircle, LogOut, Plug, CheckCircle2, KeyRound, CalendarX2, ShieldX, Archive } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';

interface NotificationItem {
  id: string;
  kind: string;
  label: string;
  severity: 'urgent' | 'normal';
  title: string;
  body: string | null;
  domain_name: string | null;
  link: string | null;
  created_at: string;
  unread: boolean;
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  renewal_failed: XCircle,
  renewal_unknown: AlertTriangle,
  ns_failed: Globe,
  ns_stalled: Globe,
  sync_failing: Server,
  transferred_out: LogOut,
  expiring_soon: CalendarClock,
  went_offline: WifiOff,
  moved_away: ArrowRightLeft,
  service_down: Plug,
  service_recovered: CheckCircle2,
  account_imported: KeyRound,
  account_archived: Archive,
  transferred_third_party: AlertTriangle,
  transferred_internal: ArrowRightLeft,
  account_needs_credentials: KeyRound,
  renewal_missed: CalendarX2,
  renewal_blocked: ShieldX,
};

const POLL_MS = 60_000;

/**
 * Sidebar bell: unread count + a panel of recent notifications the user may
 * see (the server filters by permission and domain-type scope). Opening the
 * panel doesn't mark anything read; "Mark all read" does.
 */
export default function NotificationBell({ collapsed = false, variant = 'sidebar' }: { collapsed?: boolean; variant?: 'sidebar' | 'topbar' }) {
  const { fetchApi, isSuperAdmin } = useApi();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchApi<ApiResponse<NotificationItem[]>>('/notifications?limit=30');
      setItems(res.data || []);
      setUnread((res.meta as { unread?: number } | undefined)?.unread ?? 0);
    } catch { /* keep last state */ }
  }, [fetchApi]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    load();
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, load]);

  const markAllRead = async () => {
    try {
      await fetchApi('/notifications/read-all', { method: 'POST' });
      setUnread(0);
      setItems((xs) => xs.map((x) => ({ ...x, unread: false })));
    } catch { /* ignore */ }
  };

  const badge = unread > 0 && (
    <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
      {unread > 99 ? '99+' : unread}
    </span>
  );

  const button = variant === 'topbar' ? (
    <button ref={btnRef} onClick={() => setOpen((o) => !o)} className="relative text-slate-500 hover:text-slate-700" aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}>
      <Bell className="h-5 w-5" />{badge}
    </button>
  ) : (
    <button
      ref={btnRef}
      onClick={() => setOpen((o) => !o)}
      title={collapsed ? `Notifications${unread ? ` (${unread})` : ''}` : undefined}
      aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
      className={`flex w-full items-center rounded-lg py-1.5 text-sm font-medium transition-colors ${open ? 'bg-ink-800 text-white' : 'text-ink-300 hover:bg-ink-800 hover:text-white'} ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'}`}
    >
      <span className="relative"><Bell className="h-[18px] w-[18px] text-ink-500" />{collapsed && badge}</span>
      {!collapsed && <span className="flex-1 text-left">Notifications</span>}
      {!collapsed && unread > 0 && <span className="rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">{unread > 99 ? '99+' : unread}</span>}
    </button>
  );

  // Panel floats next to the sidebar (desktop) or under the top bar (mobile).
  const rect = btnRef.current?.getBoundingClientRect();
  const style: React.CSSProperties = variant === 'topbar' || !rect
    ? { top: 60, right: 8 }
    : { top: Math.max(8, Math.min(rect.top, window.innerHeight - 520)), left: rect.right + 8 };

  return (
    <>
      {button}
      {open && createPortal(
        <div ref={panelRef} style={style} role="dialog" aria-label="Notifications"
          className="fixed z-[60] flex max-h-[min(500px,calc(100vh-1rem))] w-[min(400px,calc(100vw-1rem))] flex-col overflow-hidden rounded-xl bg-surface shadow-2xl ring-1 ring-slate-900/5 dark:ring-white/10 animate-[popIn_120ms_ease-out]">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Notifications</h2>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAllRead} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50">
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
              {isSuperAdmin && (
                <Link to="/notifications/settings" onClick={() => setOpen(false)} className="btn-ghost" title="Notification settings">
                  <Settings className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-slate-500">Nothing yet. You'll see failed renewals, transfers, sync problems and more here.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => {
                  const Icon = ICONS[n.kind] ?? Bell;
                  const content = (
                    <div className={`flex gap-3 px-4 py-3 ${n.unread ? 'bg-brand-50/40' : ''}`}>
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${n.severity === 'urgent' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-500">{n.label}</p>
                        <p className="truncate text-sm font-medium text-slate-900">{n.domain_name ?? n.title}</p>
                        {n.body && <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</p>}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-[11px] text-slate-400">{formatDistanceToNowStrict(new Date(n.created_at), { addSuffix: true })}</span>
                        {n.unread && <span className="h-2 w-2 rounded-full bg-brand-500" aria-label="Unread" />}
                      </div>
                    </div>
                  );
                  return (
                    <li key={n.id}>
                      {n.link ? <Link to={n.link} onClick={() => setOpen(false)} className="block hover:bg-slate-50">{content}</Link> : content}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
